import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import '@templates/gcsp/styles/styles.css';
import { MotifTextLink, MotifTooltip, MotifIcon } from '@ey-xd/motif-react';
import {
  FooterTable,
  GCSPHeadTable,
  TableData1C,
  TableData3DotStyle,
  TableData1B,
  GcspStatusCell,
} from '@atoms/gcsp/gcspTable.styles';
import TableHeader from '@molecules/gcsp/components/tableHeader';
import { columnDefsKeys } from '@templates/gcsp/forecast/tableComponent/columnDefs';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { LicenseManager } from 'ag-grid-enterprise';
import { getGCSProllingOpportunities } from '@services/gcspServices';
import { AG_GRID_LICENSE_KEY } from '@constants/licenseKeys';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  Gcsp_Opp_View_State,
  Current_User,
  Active_Tab_Gcsp,
  Gcsp_Opps_Details_Modal_State,
  System_Date_Time_State,
  All_Month_Data_State,
  Next_Button_Active_State,
  Message_Modal_State,
  Recoil_Gcsp_Quarter_State,
  Recoil_Selected_Account_State,
} from '@recoil/recoil';
import { formatAWDDate, formateTOV, trimAndAddDots } from '@util/commonUtil';
import GcspOpportunityNameModal from '@organisms/modals/gcspOpportunityNameModal';
import JudgeModal from '@organisms/modals/judgeModal';
import { getOpportunitiesDue } from '@services/commonServices';
import { logError } from '@util/commonFunctions';
import { FORECAST_TABLE_CONST, QUARTERLY_DETAILS } from '@constants/commonConstants';
import { isMobile } from 'react-device-detect';

import wonIcon from '@assets/icons/medal.png';
import openIcon from '@assets/icons/dashed_circle.png';
import lostIcon from '@assets/icons/block_circle.png';
import { CellWarningError } from '@atoms/tableData/styles/tableData.styles';
import { actionIcInfo24px, actionIcInfoOutline24px } from '@ey-xd/motif-react/assets/icons';
import SpinnerLoader from '../../../../atoms/SpinnerLoader';

LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);

let gridApi; // eslint-disable-line
let GcspOppsIframeUrl;
let GcspMobileOppsIframeUrl;

const GcspTable = () => {
  const topGrid = useRef(null);
  const bottomGrid = useRef(null);

  const [rowData, setRowData] = useState([]);
  //Below Line direct-import-and-test-needed
  const [columnDefs, setColumnDefs] = useState(columnDefsKeys); // eslint-disable-line
  // const setGcspOppViewData = useSetRecoilState(Gcsp_Opp_View_State);
  const [gcspOppViewData, setGcspOppViewData] = useRecoilState(Gcsp_Opp_View_State);
  const currentUser = useRecoilValue(Current_User);
  const selectedUser = useRecoilValue(Recoil_Selected_Account_State);
  const tabActive = useRecoilValue(Active_Tab_Gcsp);
  const [showGcspIframeModalRecoil, setGcspIframeModalRecoil] = useRecoilState(
    Gcsp_Opps_Details_Modal_State,
  );
  const systemDateTime = useRecoilValue(System_Date_Time_State);
  const [allMonthData, setAllMonthData] = useRecoilState(All_Month_Data_State);
  const setNextButtonActive = useSetRecoilState(Next_Button_Active_State);
  const [isLoading, setIsLoading] = useState(true);
  const setMessageModal = useSetRecoilState(Message_Modal_State);
  const [selectedOpportunityId, setSelectedOpportunityId] = useState(null); // Add this state

  const commentInputRef = useRef(null); // Ref for the comment input

  const quarterData = useRecoilValue(Recoil_Gcsp_Quarter_State);
  const TodaysDate = systemDateTime ? new Date(systemDateTime) : new Date();
  const personaStartDateCheck = quarterData ? new Date(quarterData.persona_startdate) : new Date();
  const personaCloseDateCheck = quarterData ? new Date(quarterData.persona_closedate) : new Date();
  const isReadOnly = localStorage.getItem('isReadOnly') === 'true';
  // Disable condition for Add Comment out of submit window
  const isAddCommentDisabled =
    isReadOnly ||
    !(TodaysDate >= personaStartDateCheck && TodaysDate <= personaCloseDateCheck) ||
    (localStorage.getItem('isSuperUser') == 'true' &&
      localStorage.getItem('targetUserEmail') !== 'null')
      ? true
      : false;

  // validation check for 500 chars and blank space as first char
  const JudgeCommentModal = ({ initialValue, onSave, onCancel }) => {
    const [value, setValue] = useState(initialValue || '');
    const isTooLong = value?.length > 500;
    const isFirstCharSpace = value?.startsWith(' ');

    return (
      <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
        <label
          htmlFor="judge_comment"
          style={{
            display: 'block',
            marginBottom: '10px',
            fontWeight: 'bold',
            fontSize: '14px',
            color: '#333',
          }}
        >
          Comment
        </label>
        <textarea
          id="judge_comment"
          ref={commentInputRef}
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={initialValue ? 'Edit your comment' : 'Add your comment'}
          style={{
            width: '100%',
            height: '80px',
            border: 'none',
            borderBottom: '1px solid #ccc',
            borderRadius: '0px',
            padding: '10px',
            fontSize: '14px',
            backgroundColor: '#f9f9f9',
            color: '#333',
            outline: 'none',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          <span style={{ fontSize: 12, color: '#666' }}>{value.length}/500</span>
          <span>
            {isFirstCharSpace && (
              <span role="alert" style={{ fontSize: 12, color: '#B30000', fontWeight: 600 }}>
                Comment cannot start with a space character
              </span>
            )}
            {isTooLong && (
              <span
                role="alert"
                style={{ fontSize: 12, color: '#B30000', fontWeight: 600, marginLeft: 8 }}
              >
                Maximum 500 characters allowed
              </span>
            )}
          </span>
        </div>
        <div
          style={{
            marginTop: '20px',
            display: 'flex',
            borderTop: '1px solid #ccc',
            paddingTop: '10px',
          }}
        >
          <button
            style={{
              backgroundColor: isTooLong || isFirstCharSpace ? '#E6E6E6' : '#FFD700',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: isTooLong || isFirstCharSpace ? 'not-allowed' : 'pointer',
              color: '#000',
            }}
            disabled={isTooLong || isFirstCharSpace}
            onClick={() => {
              if (isTooLong || isFirstCharSpace) return;
              onSave(value);
            }}
          >
            Save
          </button>
          <button
            style={{
              backgroundColor: '#fff',
              marginLeft: '10px',
              border: '1px solid #ccc',
              padding: '10px 20px',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: 'pointer',
              color: '#333',
            }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            style={{
              backgroundColor: '#fff',
              marginLeft: '10px',
              border: '1px solid #B30000',
              padding: '10px 20px',
              borderRadius: '4px',
              fontWeight: 'bold',
              cursor: 'pointer',
              color: '#B30000',
            }}
            onClick={() => {
              setValue('');
              onSave('');
            }}
            disabled={!initialValue}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  let newData = {};
  useEffect(() => {
    setRowData([]);
    setIsLoading(true);

    const fetchData = async () => {
      try {
        const opportunitiesDueAPI = await getOpportunitiesDue(
          currentUser.role,
          currentUser.email,
          systemDateTime.split('T')[0],
        );

        const GCSPID = currentUser.email;
        const ACCOUNTID = selectedUser?.account_id || currentUser.account_id;
        const YEAR = opportunitiesDueAPI.fiscal_year;
        const TYPE = 'rolling';
        const CURRENT_MONTH = opportunitiesDueAPI.month;

        const QUARTER = opportunitiesDueAPI.quarter;

        let START_DATE, END_DATE, MONTH;

        if (tabActive === 0) {
          START_DATE = opportunitiesDueAPI.current_month_start_date;
          END_DATE = opportunitiesDueAPI.current_month_end_date;
          MONTH = opportunitiesDueAPI.month;
        } else if (tabActive === 1) {
          START_DATE = opportunitiesDueAPI.next_month_start_date;
          END_DATE = opportunitiesDueAPI.next_month_end_dt_date;
          MONTH = opportunitiesDueAPI.next_month;
        } else {
          START_DATE = opportunitiesDueAPI.next2next_month_start_date;
          END_DATE = opportunitiesDueAPI.next2next_month_end_date;
          MONTH = opportunitiesDueAPI.next2next_month;
        }

        const getOpportunitiesDetailsAPI = await getGCSProllingOpportunities(
          GCSPID,
          ACCOUNTID,
          MONTH,
          YEAR,
          TYPE,
          START_DATE,
          END_DATE,
          CURRENT_MONTH,
          currentUser.email,
          JSON.parse(localStorage.getItem('isReadOnly')),
        );

        newData = {
          ...getOpportunitiesDetailsAPI,
          Your_Judge: getOpportunitiesDetailsAPI['Your_Judge'].map(obj => ({
            ...obj,
            month: MONTH,
            quarter: QUARTER,
            year: YEAR,
          })),
        };
        if (newData.length !== 0) {
          setNextButtonActive(true);
        }

        if (
          newData.length !== 0 &&
          allMonthData[tabActive].Total_Forecasted === null &&
          allMonthData[tabActive].Your_Judge === null &&
          allMonthData[tabActive].Total_Commit === null
        ) {
          const recoilAllMonthData = { ...allMonthData };
          recoilAllMonthData[tabActive] = newData;
          setAllMonthData(recoilAllMonthData);
        }

        setGcspOppViewData(newData);

        const rowDataCommitted = getOpportunitiesDetailsAPI.Committed.map(data => {
          if (data.is_confidential === true) {
            return {
              ...data,
              background: '#ffffff',
              opportunitynameStyle: {
                color: '#2E2E38',
                fontFamily: 'EYInterstate',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '22px', // 157.143%
              }, // Apply red color if is_confidential is true
            };
          }
          return {
            ...data,
            background: '#ffffff',
            opportunitynameStyle: {}, // Default style for non-confidential opportunities
          };
        });

        const rowDataNotCommitted = getOpportunitiesDetailsAPI.Not_Committed.map(data => {
          if (data.is_confidential === true) {
            return {
              ...data,
              background: '#f6f6fa',
              opportunitynameStyle: {
                color: '#2E2E38',
                fontFamily: 'EYInterstate',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '22px', // 157.143%
              }, // Apply red color if is_confidential is true
            };
          }
          return {
            ...data,
            background: '#f6f6fa',
            opportunitynameStyle: {}, // Default style for non-confidential opportunities
          };
        });

        const rowDataMissed = getOpportunitiesDetailsAPI.Missed.map(data => {
          if (data.is_confidential === true) {
            return {
              ...data,
              background: '#f6f6fa',
              opportunitynameStyle: {
                color: '#2E2E38',
                fontFamily: 'EYInterstate',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHheight: '22px' /* 157.143% */,
              }, // Apply red color if is_confidential is true
            };
          }
          return {
            ...data,
            background: '#f6f6fa',
            opportunitynameStyle: {}, // Default style for non-confidential opportunities
          };
        });

        const opppData = rowDataCommitted.concat(rowDataNotCommitted, rowDataMissed);

        setRowData(opppData);
        if (opppData.length !== 0) {
          expandCommit();
        }
      } catch (error) {
        logError('getOpportunitiesDue / getGCSProllingOpportunities', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [tabActive]);

  const handleGridReady = params => {
    ({ api: gridApi } = params); // eslint-disable-line
  };

  const TextCellRendererC = ({ value }) => <TableData1C>{value}</TableData1C>;
  TextCellRendererC.propTypes = {
    value: PropTypes.string.isRequired,
  };

  const PursuitTypeCellRenderer = ({ value }) => {
    // Convert pursuit types to uppercase(Brand New) for display
    const displayValue = value
      ? value
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      : '';

    return <div style={{ fontSize: '14px', color: '#2e2e38' }}>{displayValue}</div>;
  };
  PursuitTypeCellRenderer.propTypes = {
    value: PropTypes.string,
  };

  const probCellRenderer = ({ value }) => {
    if (value === '' || value === null) {
      return <TableData1C style={{ margin: 10 }}>0</TableData1C>;
    }
    return <TableData1C style={{ margin: 10 }}>{value}</TableData1C>;
  };
  TextCellRendererC.propTypes = {
    value: PropTypes.string.isRequired,
  };

  const AWD_Field_Rendere = ({ value, ...props }) => {
    const awd_date_mismatch = props?.data?.awd_date_mismatch;
    const month = props?.data?.fiscal_month;
    if (value) {
      const awd = formatAWDDate(value);
      if (awd_date_mismatch) {
        return (
          <TableData1C style={{ margin: 10 }}>
            <CellWarningError>{`${awd} (${month})`}</CellWarningError>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <MotifTooltip
                id="bottom"
                trigger={<MotifIcon src={actionIcInfo24px} className="CellWarningError" />}
                allowHover={true}
                allowClick={true}
                placement="bottom"
                hideCloseButton={true}
              >
                The Anticipated Win Date of this <br />
                opportunity has changed <br />
                since the last forecast. To see <br />
                the updated date, please <br />
                select the opportunity name. <br />
              </MotifTooltip>
            </div>
          </TableData1C>
        );
      } else {
        return <TableData1C style={{ margin: 10 }}>{`${awd} (${month})`}</TableData1C>;
      }
    }
    return <TableData1C style={{ margin: 10 }}>{}</TableData1C>;
  };
  AWD_Field_Rendere.propTypes = {
    value: PropTypes.string.isRequired,
  };

  const onOpportunityNameClick = id => {
    if (
      localStorage.getItem('isSuperUser') === 'true' &&
      localStorage.getItem('targetUserEmail') !== 'null'
    ) {
      // Set flag to use userEmail for iframe operations in super user mode
      localStorage.setItem('isIframeOperation', 'true');
      GcspOppsIframeUrl = `${process.env.REACT_APP_OPPS_NAME_IFRAME_URL}/main.aspx?appid=${process.env.REACT_APP_OPP_NAME_URL_APP_ID}&navbar=off&forceUCI=1&pagetype=entityrecord&etn=opportunity&id=${id}&formid=${process.env.REACT_APP_OPP_NAME_URL_FORM_ID}`;
      GcspMobileOppsIframeUrl = `${process.env.REACT_APP_OPPS_NAME_IFRAME_URL}/main.aspx?appid=${process.env.REACT_APP_MOBILE_OPP_NAME_URL_APP_ID}&navbar=off&forceUCI=1&pagetype=entityrecord&etn=opportunity&id=${id}&formid=${process.env.REACT_APP_OPP_NAME_URL_FORM_ID}`;
      setSelectedOpportunityId(id); // to store the id
      setGcspIframeModalRecoil(true);
    } else {
      // Regular user operation
      localStorage.setItem('isIframeOperation', 'false');
      GcspOppsIframeUrl = `${process.env.REACT_APP_OPPS_NAME_IFRAME_URL}/main.aspx?appid=${process.env.REACT_APP_OPP_NAME_URL_APP_ID}&navbar=off&forceUCI=1&pagetype=entityrecord&etn=opportunity&id=${id}&formid=${process.env.REACT_APP_OPP_NAME_URL_FORM_ID}`;
      GcspMobileOppsIframeUrl = `${process.env.REACT_APP_OPPS_NAME_IFRAME_URL}/main.aspx?appid=${process.env.REACT_APP_MOBILE_OPP_NAME_URL_APP_ID}&navbar=off&forceUCI=1&pagetype=entityrecord&etn=opportunity&id=${id}&formid=${process.env.REACT_APP_OPP_NAME_URL_FORM_ID}`;
      setSelectedOpportunityId(id); // to store the id
      setGcspIframeModalRecoil(true);
    }
  };

  const TextLinkRenderer = ({ value, data, node }) => {
    if (node.group === true) {
      return <></>;
    }

    return (
      <div
        style={{
          alignSelf: 'center',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TableData1C style={{ margin: 10, ...data.opportunitynameStyle }}>
          {data.is_confidential ? (
            // Render plain text when is_confidential is true
            <MotifTooltip
              id="bottom"
              trigger={trimAndAddDots(value)}
              allowHover={true}
              allowClick={true}
              placement="bottom"
            >
              {value}
            </MotifTooltip>
          ) : (
            // Render clickable hyperlink when is_confidential is false
            <MotifTextLink
              variant="alt"
              href="#"
              onClick={() => {
                onOpportunityNameClick(data.opportunityId);
              }}
            >
              <MotifTooltip
                id="bottom"
                trigger={trimAndAddDots(value)}
                allowHover={true}
                allowClick={true}
                placement="bottom"
              >
                {value}
              </MotifTooltip>
            </MotifTextLink>
          )}
        </TableData1C>
      </div>
    );
  };

  const CommitColumnRenderer = ({ value, data }) => {
    let dynamicBackgroundColor = '#168736';

    if (value === 'Not Committed') {
      dynamicBackgroundColor = '#C4C4CD';
      value = 'No Commit';
    } else if (value === 'Missed') {
      dynamicBackgroundColor = '#ED6500';
      value = 'Missed';
    } else {
      value = 'Commit';
    }

    return (
      <TableData1C>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TableData3DotStyle backgroundColor={dynamicBackgroundColor} />
          <div style={{ flex: 1, margin: 10 }}>{value}</div>
          {data?.unforecastedCommittedOpp && (
            <MotifTooltip
              id="missed-opp-tooltip"
              trigger={
                <MotifIcon
                  src={actionIcInfoOutline24px}
                  style={{ marginLeft: '8px', color: 'rgb(188, 47, 0)' }}
                />
              }
              allowHover={true}
              allowClick={true}
              placement="bottom"
              hideCloseButton={true}
            >
              This opportunity was <br />
              previously committed but the <br />
              PL did not submit a forecast <br />
              this cycle. It is being flagged in <br />
              case it should be included <br />
              within your judge.
            </MotifTooltip>
          )}
        </div>
      </TableData1C>
    );
  };
  // Modified truncation with tooltip and format of values
  const ServiceLineCelRenderer = ({ value }) => {
    const formattedValue = formateTOV(value);

    if (formattedValue && formattedValue.toString().length > 12) {
      return (
        <TableData1C style={{ margin: 10 }}>
          <span title={formattedValue}>{formattedValue.slice(0, 12)}...</span>
        </TableData1C>
      );
    }

    return <TableData1C style={{ margin: 10 }}>{formattedValue}</TableData1C>;
  };
  ServiceLineCelRenderer.propTypes = {
    value: PropTypes.string.isRequired,
  };

  const EmptyCelRenderer = ({ value, data }) => {
    if (data) {
      if (data.category === 'Missed') {
        return <TableData1C style={{ margin: 10 }}>{}</TableData1C>;
      }
    }
    return <TableData1C style={{ margin: 10 }}>{value}</TableData1C>;
  };
  EmptyCelRenderer.propTypes = {
    value: PropTypes.string.isRequired,
    data: PropTypes.shape({
      category: PropTypes.string,
    }),
  };

  const StatusCellRenderer = props => {
    if (props.value === 0) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspStatusCell>
            <img src={openIcon} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.OPEN}</TableData1C>
          </GcspStatusCell>
        </div>
      );
    } else if (props.value === 1) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspStatusCell>
            <img src={wonIcon} width={18} height={18} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.WON}</TableData1C>
          </GcspStatusCell>
        </div>
      );
    } else if (props.value === 2) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspStatusCell>
            <img src={lostIcon} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.LOST}</TableData1C>
          </GcspStatusCell>
        </div>
      );
    } else if (props.value === 3) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%',
          }}
        >
          <GcspStatusCell>
            <img src={lostIcon} alt="" />
            <TableData1C>{QUARTERLY_DETAILS.DECLINED}</TableData1C>
          </GcspStatusCell>
        </div>
      );
    } else {
      return <div></div>;
    }
  };

  StatusCellRenderer.propTypes = {
    value: PropTypes.number,
  };

  const getRowStyle = params => {
    if (params.node.key === 'Committed' || params.node.parent.key === 'Committed') {
      // For row groups
      return { background: '#ffffff' };
    } else {
      // For inner elements
      return { background: '#eaeaf2' };
    }
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: 'PL Commit Status',
      minWidth: 10,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: 'commitColumnRenderer',
      },
      suppressMenu: true,
      suppressMovable: true,
    };
  }, []);

  const groupRowRenderer = params => {
    return params.node.group
      ? `Custom Group: ${params.node.key}`
      : params.data[params.colDef.field];
  };

  const expandCommit = useCallback(() => {
    clearTimeout();
    setTimeout(() => {
      if (topGrid.current.api) {
        topGrid.current.api.forEachNode(node => {
          if (node.key === 'Committed') {
            if (node.expanded !== true) {
              topGrid.current.api.setRowNodeExpanded(node, true, true);
            }
          }
        });
      }
    }, 100);
  }, []);

  //   Footer Table Code Start

  let footerTableRowData = [];
  if (
    gcspOppViewData.length !== 0 &&
    allMonthData[tabActive].Total_Forecasted !== null &&
    allMonthData[tabActive].Your_Judge !== null &&
    allMonthData[tabActive].Total_Commit !== null
  ) {
    const rowDataTotalForecasted = allMonthData[tabActive].Total_Forecasted.map(data => ({
      ...data,
      background: '#ffffff',
    }));

    const rowDataTotalYourJudge = allMonthData[tabActive].Your_Judge.map(data => ({
      ...data,
      background: '#ffffff',
    }));
    const rowDataTotalCommit = allMonthData[tabActive].Total_Commit.map(data => ({
      ...data,
      background: '#ffffff',
    }));
    footerTableRowData = rowDataTotalForecasted.concat(rowDataTotalYourJudge, rowDataTotalCommit);
  }

  const ColumnHeaderRenderer = ({ data }) => {
    const keyToText = {
      'Total Forecasted': 'PL Total Committed + Audit/TR Forecast',
      'Your Judge': 'Your Judge',
      'Total Commit': 'Your Total Commit',
    };
    const category = data.category;
    const displayText = keyToText[category] || '';

    // Determine if comment exists
    const existingComment = data?.judge_comment || '';
    const buttonLabel = existingComment ? 'Edit Comment' : 'Add Comment';
    return (
      <TableData1B>
        <div style={{ display: 'flex', alignItems: 'center', paddingTop: '10px' }}>
          <span>{displayText}</span>
          {category === 'Your Judge' && (
            <button
              href="#"
              disabled={isAddCommentDisabled}
              onClick={e => {
                e.preventDefault();
                if (isAddCommentDisabled) return;
                handleAddCommentClick(); // Open the modal when the hyperlink is clicked
              }}
              style={{
                marginLeft: '10px',
                fontSize: '14px',
                color: isAddCommentDisabled ? '#8a8a94' : '#0073e6',
                textDecoration: 'underline',
                cursor: isAddCommentDisabled ? 'not-allowed' : 'pointer',
                background: 'none',
                border: 'none',
                padding: '0',
                opacity: isAddCommentDisabled ? 0.6 : 1,
              }}
            >
              {buttonLabel}
            </button>
          )}
        </div>
      </TableData1B>
    );
  };

  const OnlyCommittedRenderer = ({ data }) => {
    const dynamicBackgroundColor = '#168736';
    const keyToText = {
      'Total Forecasted':
        'Totals include Commits and Audit and Tech. Risk opportunities that are Renewals and/or have a probability of 100%',
    };
    const category = data.category;
    const displayText = keyToText[category] || '';

    return (
      <TableData1C>
        <div style={{ display: 'flex', alignItems: 'center', paddingTop: '10px' }}>
          {displayText ? <TableData3DotStyle backgroundColor={dynamicBackgroundColor} /> : null}
          {displayText ? <span>{displayText}</span> : null}
        </div>
      </TableData1C>
    );
  };

  const FooterTextCellRendererC = props => {
    const { value, data, KeyName } = props;
    if (data.category === FORECAST_TABLE_CONST.GCSP_JUDGE) {
      return <JudgeModal KeyName={KeyName} />;
    } else {
      return formateTOV(value);
    }
  };

  FooterTextCellRendererC.propTypes = {
    value: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
  };

  const TOVCellRenderer = props => {
    const { value, assurance, consulting, sat, tax, itts, EMIAACR, KeyName, data } = props;
    let TotalTov =
      Number(assurance) +
      Number(consulting) +
      Number(sat) +
      Number(tax) +
      Number(itts) +
      Number(EMIAACR);

    TotalTov = formateTOV(TotalTov);

    if (data.category === 'Your Judge' && KeyName === 'TOV') {
      return <TableData1C style={{ fontWeight: 'bold' }}>{TotalTov} </TableData1C>;
    } else if (data.category === 'Total Forecasted' && KeyName === 'TOV') {
      return <TableData1C style={{ fontWeight: 'bold' }}>{TotalTov} </TableData1C>;
    } else if (data.category === 'Total Commit' && KeyName === 'TOV') {
      return <TableData1C style={{ fontWeight: 'bold' }}>{TotalTov} </TableData1C>;
    } else {
      return formateTOV(value);
    }
  };

  TOVCellRenderer.propTypes = {
    value: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
  };

  const cellTextAligment = { display: 'flex', justifyContent: 'flex-end', alignItems: 'center' };

  const footerTableColumnDefs = [
    {
      headerName: '',
      width: 300,
      cellRenderer: ColumnHeaderRenderer,
      resizable: false,
      headerComponentParams: {
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: '',
      width: 1130,
      cellRenderer: OnlyCommittedRenderer,
      headerComponentParams: {
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'Assurance',
      field: 'Assurance',
      width: 140,
      id: 1,
      cellRenderer: 'textCellRendererC',
      cellRendererParams: () => {
        const KeyName = 'Assurance';
        return {
          KeyName,
        };
      },
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'Assurance',
        subText: 'excl. ACR',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'Consulting',
      field: 'consulting',
      width: 140,
      cellRenderer: 'textCellRendererC',
      cellRendererParams: () => {
        const KeyName = 'consulting';
        return {
          KeyName,
        };
      },
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'Consulting',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'SAT',
      field: 'sat',
      width: 140,
      cellRenderer: 'textCellRendererC',
      cellRendererParams: () => {
        const KeyName = 'sat';
        return {
          KeyName,
        };
      },
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'SAT',
        subText: 'excl. ITTS',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'Tax',
      field: 'tax',
      width: 130,
      cellRenderer: 'textCellRendererC',
      cellRendererParams: () => {
        const KeyName = 'tax';
        return {
          KeyName,
        };
      },
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'Tax',
        subText: 'excl. ITTS',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'ITTS',
      field: 'itts',
      width: 130,
      cellRenderer: 'textCellRendererC',
      cellStyle: cellTextAligment,
      cellRendererParams: () => {
        const KeyName = 'itts';
        return {
          KeyName,
        };
      },
      headerComponentParams: {
        displayName: 'ITTS',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'EMEIA ACR',
      field: 'EMIAACR',
      width: 150,
      cellRenderer: 'textCellRendererC',
      cellStyle: cellTextAligment,
      cellRendererParams: () => {
        const KeyName = 'EMIAACR';
        return {
          KeyName,
        };
      },
      headerComponentParams: {
        displayName: 'EMEIA ACR',
        enableSorting: false,
        enableMenu: false,
      },
    },
    {
      headerName: 'Total',
      field: 'TOV',
      width: 140,
      cellRenderer: 'tovCellRenderer',
      cellStyle: cellTextAligment,
      headerComponentParams: {
        displayName: 'Total',
        enableSorting: false,
        enableMenu: false,
      },
      cellRendererParams: params => {
        const KeyName = 'TOV';
        const assurance = params.data.Assurance;
        const consulting = params.data.consulting;
        const sat = params.data.sat;
        const tax = params.data.tax;
        const itts = params.data.itts;
        const EMIAACR = params.data.EMIAACR;
        return {
          KeyName,
          assurance,
          consulting,
          sat,
          tax,
          itts,
          EMIAACR,
        };
      },
    },
  ];

  const getFooterTableRowStyle = params => {
    return { background: params.data.background };
  };

  const handleAddCommentClick = async () => {
    let MONTH;
    const opportunitiesDueAPI = await getOpportunitiesDue(
      currentUser.role,
      currentUser.email,
      systemDateTime.split('T')[0],
    );

    if (tabActive === 0) {
      MONTH = opportunitiesDueAPI.month; // Current month
    } else if (tabActive === 1) {
      MONTH = opportunitiesDueAPI.next_month; // Next month
    } else if (tabActive === 2) {
      MONTH = opportunitiesDueAPI.next2next_month; // Next-to-next month
    }

    const existingComment = allMonthData[tabActive]?.Your_Judge?.[0]?.judge_comment || '';

    // Open the modal dynamically with validation
    setMessageModal({
      isOpen: true,
      className: 'gcsp-message-modal',
      headerTitle: `Add Comment : ${MONTH}`,
      bodyContent: (
        <JudgeCommentModal
          initialValue={existingComment}
          onSave={value => {
            // Create a deep copy of allMonthData
            const updatedAllMonthData = JSON.parse(JSON.stringify(allMonthData));

            // Update the judge_comment field in the Your_Judge object for the current tab
            if (updatedAllMonthData[tabActive]?.Your_Judge?.[0]) {
              updatedAllMonthData[tabActive].Your_Judge[0].judge_comment = value;
            }

            // Update the Recoil state with the modified object
            setAllMonthData(updatedAllMonthData);

            setMessageModal({ isOpen: false });
          }}
          onCancel={() => {
            document.body.style.overflow = 'auto';
            setMessageModal({ isOpen: false });
          }}
        />
      ),
    });
  };

  return (
    <>
      <GCSPHeadTable className="ag-theme-alpine " id="gcspheadtable">
        {isLoading ? (
          <div style={{ position: 'fixed', top: '50%', left: '50%' }}>
            <SpinnerLoader color="#155CB4" />
          </div>
        ) : (
          <AgGridReact
            ref={topGrid}
            alignedGrids={[bottomGrid]}
            firstColumnBorderRight
            columnDefs={columnDefs}
            rowData={rowData}
            groupDisplayType="singleColumn"
            groupRowRenderer={groupRowRenderer}
            showOpenedGroup={true}
            autoGroupColumnDef={autoGroupColumnDef}
            onGridReady={handleGridReady}
            headerHeight={70}
            headerCellWidth={200}
            components={{
              agColumnHeader: TableHeader,
              textCellRendererC: TextCellRendererC,
              pursuitTypeCellRenderer: PursuitTypeCellRenderer,
              textLinkRenderer: TextLinkRenderer,
              commitColumnRenderer: CommitColumnRenderer,
              awd_Field_Rendere: AWD_Field_Rendere,
              serviceLineCelRenderer: ServiceLineCelRenderer,
              emptyCelRenderer: EmptyCelRenderer,
              probCellRenderer: probCellRenderer,
              statusCellRenderer: StatusCellRenderer,
            }}
            getRowStyle={getRowStyle}
            alwaysShowVerticalScroll={true}
            // overlayNoRowsTemplate={showNowRowsToShowText ? 'Loading...' : 'No Rows To Show'}
          />
        )}
        {showGcspIframeModalRecoil ? (
          <GcspOpportunityNameModal
            id={selectedOpportunityId}
            urlSource={isMobile ? GcspMobileOppsIframeUrl : GcspOppsIframeUrl}
          />
        ) : null}
      </GCSPHeadTable>
      <FooterTable className="ag-theme-alpine" id="gcspfootertable">
        <AgGridReact
          ref={bottomGrid}
          alignedGrids={[topGrid]}
          columnDefs={footerTableColumnDefs}
          rowData={footerTableRowData}
          components={{
            agColumnHeader: TableHeader,
            textCellRendererC: FooterTextCellRendererC,
            tovCellRenderer: TOVCellRenderer,
          }}
          headerHeight={60}
          headerCellWidth={200}
          getRowStyle={getFooterTableRowStyle}
          alwaysShowHorizontalScroll={true}
          overlayNoRowsTemplate="Loading..."
          domLayout="autoHeight"
        />
      </FooterTable>
    </>
  );
};

export default GcspTable;
